import React, { useCallback, useState } from "react";
import MainLayout from "../../components/layout/MainLayout";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { CircularProgress } from "@mui/material";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import Login from "../../app/auth/Login";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";

const DrawerHeader = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

const AIEmailIntegration = () => {
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [csvData, setCsvData] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setMessage("");
      setCsvData(null);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "application/pdf": [] },
    multiple: true,
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage("Please select a PDF file first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setUploading(true);
      const response = await axios.post(
        `https://aiemailintegration.azurewebsites.net/invoice_ui`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        setCsvData(response.data);
        setMessage("CSV file generated successfully!");
        setOpenUploadModal(false);
      }
    } catch (error) {
      setMessage("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const downloadCSV = () => {
    if (!csvData || !selectedFile) return;

    const timestamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedFile.name}-${timestamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handlePreview = () => {
    if (!csvData || !selectedFile) return;
    
    const timestamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary link to open with proper filename
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedFile.name}-${timestamp}.csv`;
    a.target = '_blank';
    a.click();
    
    // Clean up
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <>
      <AuthenticatedTemplate>
        <div className="h-[var(--app-height)] overflow-hidden">
          <Box className="flex h-full">
            {/* Sidebar */}
            <MainLayout />

            {/* Main content */}
            <Box component="main" className="flex-1 bg-white px-6 py-4 overflow-hidden">
              <DrawerHeader />

              {/* Header */}
              <div className="flex items-start justify-between border-b border-gray-200 pb-4">
                {/* Left */}
                <div>
                  <div className="flex items-center gap-2">
                    <div className="bg-[#C800DE] p-[4px] mt-[-9px] inline-flex items-center justify-center cursor-pointer rounded-full">
                      <ArrowBackIcon sx={{ fontSize: "8px", color: "#fff" }} />
                    </div>
                    <h1 className="text-lg font-semibold text-[#656565]">
                      Ap Invoice
                    </h1>
                  </div>
                  {selectedFile && (
                    <p className="text-xs text-gray-500 mt-1 ml-8">
                      Document processed on {new Date().toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })} at {new Date().toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit', 
                        hour12: true 
                      })}
                    </p>
                  )}
                </div>

                {/* Right buttons */}
                <div className="flex items-center gap-2">
                  <button
                    disabled={csvData === null}
                    onClick={handlePreview}
                    className={`flex items-center gap-2 px-2 py-2 text-sm border rounded-[12px] ${
                      csvData === null
                        ? "border-[#818181] text-[#818181] cursor-not-allowed opacity-50"
                        : "border-[#bb1ccc] text-[#bb1ccc] cursor-pointer hover:bg-[#bb1ccc] hover:text-white"
                    }`}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: "14px" }} />
                    Preview
                  </button>

                  <button
                    disabled
                    className="flex items-center gap-2 px-2 text-[12px] py-2 text-sm border border-[#818181] rounded-[12px] text-[#818181] cursor-not-allowed opacity-50"
                  >
                    <ShareOutlinedIcon sx={{ fontSize: "14px" }} />
                    Share
                  </button>

                  <button
                    disabled={csvData === null}
                    onClick={downloadCSV}
                    className={`flex items-center gap-2 px-2 py-2 text-sm border rounded-[12px] ${
                      csvData === null
                        ? "border-[#818181] text-[#818181] cursor-not-allowed opacity-50"
                        : "border-[#bb1ccc] text-[#bb1ccc] cursor-pointer hover:bg-[#bb1ccc] hover:text-white"
                    }`}
                  >
                    <FileDownloadOutlinedIcon sx={{ fontSize: "14px" }} />
                    Export
                  </button>

                  <button
                    onClick={() => setOpenUploadModal(true)}
                    className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-white bg-[#bb1ccc] rounded-lg hover:bg-[#a91bb5]"
                  >
                    <UploadFileIcon sx={{ fontSize: "14px" }} />
                    Upload PDF
                  </button>
                </div>
              </div>

              {/* Analysis Complete Section - Only show when CSV is generated */}
              {csvData && (
                <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="12" cy="12" r="10" fill="#10B981" />
                          <path
                            d="M9 12L11 14L15 10"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          Analysis Complete
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          All compliance checks passed with minor recommendations
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-md">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs font-medium text-green-700">
                        Processing Complete
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </Box>
          </Box>

          {/* Upload Modal */}
          <Dialog
            open={openUploadModal}
            onClose={() => {
              setOpenUploadModal(false);
              setSelectedFile(null);
              setMessage("");
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                mt: 10,
                ml: 20,
              },
            }}
          >
            <DialogContent>
              <div className="p-4">
                <h2 className="text-lg font-semibold text-[#1a1a1a]">
                  Upload PDF File
                </h2>

                <div
                  {...getRootProps()}
                  className="mt-6 flex flex-col items-center justify-center
                             border-2 border-dashed border-gray-300 rounded-xl
                             h-[140px] cursor-pointer text-gray-500"
                >
                  <input {...getInputProps()} />
                  <span>
                    {selectedFile ? "File selected" : "Drop or Upload your PDF here"}
                  </span>
                </div>

                {selectedFile && (
                  <p className="mt-2 text-sm text-[#bb1ccc]">
                    File Name:{" "}
                    <span className="font-medium">{selectedFile.name}</span>
                  </p>
                )}

              
              </div>
            </DialogContent>

            <DialogActions className="px-6 pb-4">
              <button
                onClick={() => {
                  setOpenUploadModal(false);
                  setSelectedFile(null);
                  setMessage("");
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg text-sm"
                disabled={uploading}
              >
                Cancel
              </button>

              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className="px-6 py-2 bg-[#bb1ccc] text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  "Generate CSV"
                )}
              </button>
            </DialogActions>
          </Dialog>
        </div>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </>
  );
};

export default AIEmailIntegration;